import React, { useEffect, useRef } from "react";
import analytics from "analytics/analytics";
import browser from "utils/browser";
import Slider from "react-slick";
import ReactImageMagnify from "react-image-magnify";
import "./slick.less";
import "./slick-theme.less";
import styles from "./image-gallery.css";
import PropTypes from "prop-types";
const longPressDuration = 500;

function ZoomImage({ image }) {
    return (
        <div>
            <ReactImageMagnify
                {...{
                    smallImage: {
                        alt: image.altText,
                        isFluidWidth: true,
                        src: browser.retina ? image.large_url_2x : image.large_url
                    },
                    largeImage: {
                        alt: image.altText,
                        src: image.large_url_2x,
                        width: 1040,
                        height: 1300
                    },
                    isHintEnabled: true,
                    shouldHideHintAfterFirstActivation: false,
                    enlargedImagePosition: "over",
                    pressDuration: longPressDuration
                }}
            />
        </div>
    );
}

function ImageGallery({ images }) {
    const viewport = browser.getViewport() === browser.VIEWPORTS.DESKTOP ? "desktop" : "mobile";
    const analyticsAction = `${viewport}_gallery`;
    const lastImageIndex = images.length - 1;

    let firstInteraction = false;
    let seenAll = false;
    let zoomedOnce = false;
    let imagesSeen = 0;
    let longPressTime = 0;
    let firstClientX;

    // Variable to check if touch is long press or swipe
    let hasTouchMoved = false;

    const wrapper = useRef(null);

    const settings = {
        speed: 500,
        slidesToShow: 1,
        infinite: true,
        arrows: false,
        dots: true,
        lazyLoad: true,
        centerMode: false,
        touchThreshold: 5
    };

    const zoomElements = images.map((image, index) => {
        return <ZoomImage key={`zoomimg-${index}`} image={image} />;
    });

    useEffect(() => {
        const wrapperRef = wrapper.current;
        // I cannot make the preventing of scroll in Safari working with the React SyntheticEvent onTouchMove
        // so I need to use this event listener for touch move event
        wrapperRef.addEventListener("touchmove", handleTouchMoved, { passive: false });
        wrapperRef.addEventListener("gesturestart", preventPinchZoom);
        return () => {
            wrapperRef.removeEventListener("touchmove", handleTouchMoved, { passive: false });
            wrapperRef.removeEventListener("gesturestart", preventPinchZoom);
        };
    });

    function handleTouchStart(event) {
        hasTouchMoved = false;
        if (event && event.touches.length > 0) {
            firstClientX = event.touches[0].clientX;

            if (!zoomedOnce) {
                longPressTime = event.timeStamp;
            }
        }
    }

    function handleTouchMoved(event) {
        hasTouchMoved = true;
        if (event.touches.length > 0) {
            const minValue = settings.touchThreshold;
            const clientX = event.touches[0].clientX - firstClientX;

            // Disable vertical scrolling when user swipes horizontally (Safari fix).
            if (Math.abs(clientX) > minValue) {
                event.preventDefault();
                event.returnValue = false;
                return false;
            }
        }
    }

    function handleTouchEnd(event) {
        if (zoomedOnce || hasTouchMoved || !event) {
            return;
        }
        longPressTime = event.timeStamp - longPressTime;
        if (longPressTime >= longPressDuration) {
            zoomedOnce = true;
            analytics.event("engagement", analyticsAction, "zoomed_in_gallery");
            if (!firstInteraction) {
                firstInteraction = true;
                analytics.event("engagement", analyticsAction, "interacted");
            }
        }
    }

    function preventPinchZoom(event) {
        // Disable zoom (also in Safari, all others have disabled zoom everywhere) inside gallery
        event.preventDefault();
    }

    function onImageChange(currentSlide, nextSlide) {
        analytics.event("engagement", analyticsAction, "changed_image");

        if (!firstInteraction) {
            firstInteraction = true;
            analytics.event("engagement", analyticsAction, "interacted");
        }

        if (!seenAll) {
            if (nextSlide === currentSlide + 1) {
                // User scrolls right
                imagesSeen += 1;
            } else if (nextSlide === currentSlide - 1 || nextSlide === lastImageIndex) {
                // User scrolls left
                imagesSeen -= 1;
            }

            if (Math.abs(imagesSeen) === lastImageIndex) {
                seenAll = true;
                analytics.event("engagement", analyticsAction, "seen_all");
            }
        }
    }

    return (
        <div
            ref={wrapper}
            className={styles.carousel}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <Slider beforeChange={onImageChange} {...settings}>
                {zoomElements}
            </Slider>
        </div>
    );
}

export default ImageGallery;

ImageGallery.propTypes = {
    images: PropTypes.array.isRequired
};

ZoomImage.propTypes = {
    image: PropTypes.shape({
        large_url: PropTypes.string.isRequired,
        large_url_2x: PropTypes.string.isRequired,
        alt_text: PropTypes.string.isRequired
    })
};
